/* eslint-disable import/prefer-default-export, no-await-in-loop, no-loop-func, consistent-return */
/**
 * Comprehensive API Error Handling Helper
 *
 * Provides robust error handling, retry mechanisms, and user-friendly error messages
 * for API interactions throughout the application, with special focus on offer creation.
 *
 * Features:
 * - Exponential backoff retry logic
 * - User-friendly error categorization
 * - Request cancellation support
 * - Progress tracking for multi-step operations
 * - Accessibility-focused error messaging
 * - Comprehensive logging for debugging
 *
 * Usage:
 * import apiErrorHandler from '@/helper/apiErrorHandler'
 * const { retryWithBackoff, handleApiError } = apiErrorHandler()
 */

import axios from 'axios'

export default function apiErrorHandler() {
  // Error categories for user-friendly messaging
  const ErrorCategories = {
    NETWORK: 'network',
    TIMEOUT: 'timeout',
    SERVER: 'server',
    CLIENT: 'client',
    AUTHORIZATION: 'authorization',
    VALIDATION: 'validation',
    ZOHO: 'zoho',
    UNKNOWN: 'unknown'
  }

  // Retry configurations for different error types
  const RetryConfig = {
    [ErrorCategories.NETWORK]: { maxRetries: 3, initialDelay: 1000, maxDelay: 8000 },
    [ErrorCategories.TIMEOUT]: { maxRetries: 2, initialDelay: 2000, maxDelay: 6000 },
    [ErrorCategories.SERVER]: { maxRetries: 2, initialDelay: 1500, maxDelay: 5000 },
    [ErrorCategories.ZOHO]: { maxRetries: 3, initialDelay: 1000, maxDelay: 10000 },
    default: { maxRetries: 1, initialDelay: 1000, maxDelay: 3000 }
  }

  /**
   * Categorize error for appropriate handling strategy
   */
  function categorizeError(error) {
    if (!error) return ErrorCategories.UNKNOWN

    // Check if request was cancelled
    if (axios.isCancel(error)) {
      return ErrorCategories.CLIENT // Don't retry cancelled requests
    }

    // Network/connection errors
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
      return ErrorCategories.TIMEOUT
    }

    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return ErrorCategories.NETWORK
    }

    // HTTP status code based categorization
    if (error.response?.status) {
      const {status} = error.response

      if (status === 401 || status === 403) return ErrorCategories.AUTHORIZATION
      if (status === 400 || status === 422) return ErrorCategories.VALIDATION
      if (status >= 500) return ErrorCategories.SERVER
      if (status >= 400) return ErrorCategories.CLIENT
    }

    // Zoho-specific errors
    if (error.message?.toLowerCase().includes('zoho')
        || error.config?.url?.includes('sign.der-lagermeister.de')) {
      return ErrorCategories.ZOHO
    }

    return ErrorCategories.UNKNOWN
  }

  /**
   * Get user-friendly error message based on error category and context
   */
  function getUserFriendlyMessage(error, context = '') {
    const category = categorizeError(error)
    const contextPrefix = context ? `${context}: ` : ''

    const messages = {
      [ErrorCategories.NETWORK]: {
        title: 'Verbindungsproblem',
        message: `${contextPrefix}Keine Internetverbindung oder Server nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.`,
        recovery: 'Versuchen Sie es in wenigen Sekunden erneut.'
      },
      [ErrorCategories.TIMEOUT]: {
        title: 'Zeitüberschreitung',
        message: `${contextPrefix}Die Anfrage dauerte zu lange. Der Server ist möglicherweise überlastet.`,
        recovery: 'Versuchen Sie es erneut oder warten Sie einen Moment.'
      },
      [ErrorCategories.SERVER]: {
        title: 'Server-Fehler',
        message: `${contextPrefix}Ein temporärer Serverfehler ist aufgetreten.`,
        recovery: 'Der Fehler sollte sich von selbst lösen. Versuchen Sie es in wenigen Minuten erneut.'
      },
      [ErrorCategories.AUTHORIZATION]: {
        title: 'Berechtigung fehlt',
        message: `${contextPrefix}Ihre Sitzung ist abgelaufen oder Sie haben keine Berechtigung.`,
        recovery: 'Bitte loggen Sie sich erneut ein.'
      },
      [ErrorCategories.VALIDATION]: {
        title: 'Ungültige Daten',
        message: `${contextPrefix}Die übermittelten Daten sind unvollständig oder fehlerhaft.`,
        recovery: 'Bitte prüfen Sie alle Eingabefelder und versuchen Sie es erneut.'
      },
      [ErrorCategories.ZOHO]: {
        title: 'Vertragssystem nicht verfügbar',
        message: `${contextPrefix}Das externe Vertragssystem ist momentan nicht erreichbar.`,
        recovery: 'Versuchen Sie es in einigen Minuten erneut oder kontaktieren Sie den Support.'
      },
      [ErrorCategories.CLIENT]: {
        title: 'Fehlerhafte Anfrage',
        message: `${contextPrefix}Die Anfrage konnte nicht verarbeitet werden.`,
        recovery: 'Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.'
      },
      [ErrorCategories.UNKNOWN]: {
        title: 'Unbekannter Fehler',
        message: `${contextPrefix}Ein unerwarteter Fehler ist aufgetreten.`,
        recovery: 'Versuchen Sie es erneut oder kontaktieren Sie den Support falls das Problem bestehen bleibt.'
      }
    }

    return messages[category] || messages[ErrorCategories.UNKNOWN]
  }

  /**
   * Calculate delay for exponential backoff with jitter
   */
  function calculateBackoffDelay(attempt, config) {
    const { initialDelay, maxDelay } = config
    const exponentialDelay = initialDelay * (2 ** (attempt - 1))
    const jitter = Math.random() * 0.3 * exponentialDelay // 30% jitter
    return Math.min(exponentialDelay + jitter, maxDelay)
  }

  /**
   * Sleep function for delays
   */
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Check if error is retryable based on category
   */
  function isRetryable(error) {
    const category = categorizeError(error)
    return ![ErrorCategories.AUTHORIZATION, ErrorCategories.VALIDATION, ErrorCategories.CLIENT].includes(category)
  }

  /**
   * Advanced retry mechanism with exponential backoff and progress callbacks
   */
  async function retryWithBackoff(
    apiCall,
    options = {},
    progressCallback = null,
    cancelToken = null
  ) {
    const {
      context = '',
      customRetryConfig = null,
      onRetry = null,
      onError = null
    } = options

    let attempt = 0
    const maxAttempts = 10 // Reasonable maximum to prevent infinite loops

    while (attempt < maxAttempts) {
      attempt += 1

      try {
        // Check if operation was cancelled
        if (cancelToken?.isCancelled) {
          throw new Error('Operation cancelled by user')
        }

        const result = await apiCall()

        // Success - return result
        if (progressCallback) {
          progressCallback({
            type: 'success',
            attempt,
            message: `${context} erfolgreich abgeschlossen`
          })
        }

        return result
      } catch (error) {
        const category = categorizeError(error)
        const config = customRetryConfig || RetryConfig[category] || RetryConfig.default

        // Log error for debugging
        console.error(`API Error (Attempt ${attempt}):`, {
          context,
          category,
          error: error.message,
          config: error.config?.url
        })

        // Check if we should retry
        if (!isRetryable(error) || attempt >= config.maxRetries) {
          // Final failure
          if (progressCallback) {
            const friendlyError = getUserFriendlyMessage(error, context)
            progressCallback({
              type: 'error',
              attempt,
              error: friendlyError,
              technical: error.message
            })
          }

          if (onError) {
            onError(error, attempt)
          }

          throw error
        }

        // Calculate backoff delay
        const delay = calculateBackoffDelay(attempt, config)

        // Notify about retry
        if (progressCallback) {
          progressCallback({
            type: 'retry',
            attempt,
            nextAttempt: attempt + 1,
            maxRetries: config.maxRetries,
            delay: Math.round(delay / 1000),
            message: `Wiederhole ${context} (Versuch ${attempt + 1} von ${config.maxRetries})`
          })
        }

        if (onRetry) {
          onRetry(error, attempt, delay)
        }

        // Wait before retry
        await sleep(delay)
      }
    }
  }

  /**
   * Handle API error with comprehensive error processing
   */
  function handleApiError(error, context = '') {
    const category = categorizeError(error)
    const friendlyMessage = getUserFriendlyMessage(error, context)

    // Log technical details for debugging
    console.error('API Error Details:', {
      context,
      category,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      stack: error.stack
    })

    return {
      category,
      friendlyMessage,
      technical: {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      },
      isRetryable: isRetryable(error),
      timestamp: new Date().toISOString()
    }
  }

  /**
   * Create a cancellable token for long-running operations
   */
  function createCancellationToken() {
    let isCancelled = false
    let cancelReason = null

    return {
      get isCancelled() {
        return isCancelled
      },
      get reason() {
        return cancelReason
      },
      cancel(reason = 'Operation cancelled') {
        isCancelled = true
        cancelReason = reason
      },
      throwIfCancelled() {
        if (isCancelled) {
          throw new Error(cancelReason)
        }
      }
    }
  }

  /**
   * Batch API requests with error handling and progress tracking
   */
  async function batchRequests(
    requests,
    options = {},
    progressCallback = null,
    cancelToken = null
  ) {
    const {
      maxConcurrency = 3,
      continueOnError = false,
      context = 'Batch operation'
    } = options

    const results = []
    const errors = []
    let completed = 0

    // Process requests in batches
    for (let i = 0; i < requests.length; i += maxConcurrency) {
      const batch = requests.slice(i, i + maxConcurrency)

      const batchPromises = batch.map(async (request, batchIndex) => {
        const overallIndex = i + batchIndex

        try {
          if (cancelToken?.isCancelled) {
            throw new Error('Batch operation cancelled')
          }

          const result = await retryWithBackoff(
            request.apiCall,
            {
              context: request.context || `${context} ${overallIndex + 1}`,
              ...request.options
            },
            progressCallback,
            cancelToken
          )

          completed += 1

          if (progressCallback) {
            progressCallback({
              type: 'progress',
              completed,
              total: requests.length,
              current: overallIndex + 1,
              message: `${completed} von ${requests.length} abgeschlossen`
            })
          }

          return { index: overallIndex, success: true, result }
        } catch (error) {
          completed += 1
          const errorInfo = handleApiError(error, request.context || `${context} ${overallIndex + 1}`)

          if (progressCallback) {
            progressCallback({
              type: 'itemError',
              completed,
              total: requests.length,
              current: overallIndex + 1,
              error: errorInfo
            })
          }

          if (!continueOnError) {
            throw error
          }

          return { index: overallIndex, success: false, error: errorInfo }
        }
      })

      const batchResults = await Promise.allSettled(batchPromises)

      batchResults.forEach(result => {
        if (result.status === 'fulfilled') {
          if (result.value.success) {
            results[result.value.index] = result.value.result
          } else {
            errors[result.value.index] = result.value.error
          }
        } else {
          // This shouldn't happen with our current setup, but handle it anyway
          errors[results.length + errors.length] = {
            category: ErrorCategories.UNKNOWN,
            friendlyMessage: getUserFriendlyMessage(result.reason),
            technical: { message: result.reason?.message || 'Unknown batch error' }
          }
        }
      })
    }

    return {
      results,
      errors,
      hasErrors: errors.length > 0,
      successCount: results.filter(r => r !== undefined).length,
      errorCount: errors.filter(e => e !== undefined).length
    }
  }

  /**
   * Create axios request interceptor with automatic retry
   */
  function createRetryInterceptor(axiosInstance) {
    axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        const {config} = error

        // Don't retry if already retried or not retryable
        if (config.retryCount >= 3 || !isRetryable(error)) {
          return Promise.reject(error)
        }

        config.retryCount = config.retryCount || 0
        config.retryCount += 1

        const category = categorizeError(error)
        const retryConfig = RetryConfig[category] || RetryConfig.default
        const delay = calculateBackoffDelay(config.retryCount, retryConfig)

        await sleep(delay)
        return axiosInstance(config)
      }
    )
  }

  // Export all functions
  return {
    // Core functions
    retryWithBackoff,
    handleApiError,
    categorizeError,
    getUserFriendlyMessage,

    // Utilities
    createCancellationToken,
    batchRequests,
    createRetryInterceptor,
    isRetryable,

    // Constants
    ErrorCategories,
    RetryConfig
  }
}
